import { createContext, useContext, useEffect } from "react"

import { count, find } from "./Iterator"
import { useForceUpdate, useInterval, useTabFocus } from "./React"

const debug = false

/**
 * Higher level, Suspense abstraction over request.
 * Supports:
 *   - LRU cache eviction, per fetcher.
 *   - Different revalidation strategies, per fetcher.
 *   - request pooling
 */
export function useRequest<ARGS extends readonly SerializableArg[], BODY>(
  fetcher: Fetcher<ARGS, BODY>,
  args: ARGS,
): BODY {
  useRevalidateDataOnFocus(fetcher, args)
  useSubscribeToUpdates(fetcher, shouldUpdate)
  useRegisterRequestWithBoundary(fetcher, args)

  function shouldUpdate(updatedArgs: ARGS) {
    const ownArgsKey = argsKey(fetcher, args)
    const forArgsKey = argsKey(fetcher, updatedArgs)
    return ownArgsKey === forArgsKey
  }

  const cacheValue = _cache.get(fetcher, args)

  // This data is unknown to the cache, time to fetch it.
  if (!cacheValue) {
    if (debug) {
      const name = `${fetcher.name}(${args.join(", ")})`
      console.debug(`${name} was called without a previous preload()`)
    }
    throw initNewFetch(fetcher, args)
  }

  // The request is already in flight: suspend.
  if (cacheValue.state === "fetching") throw cacheValue.promise

  // We have an OK result already (whether fetched or revalidating), synchronously return it.
  if (cacheValue.ok) {
    // If it's stale, return immediately but refetch in the background.
    if (cacheValue.stale) {
      revalidate(fetcher, args, "wasStale")
        ?.then(() => notifyAll(fetcher, args))
        .catch(() => {})
    }
    return cacheValue.body
  }

  // We already have an error in cache, let an Error Boundary catch it.
  throw cacheValue.error
}

/**
 * Starts loading data in the background, without blocking Suspense.
 */
export function preload<ARGS extends readonly SerializableArg[], BODY>(
  fetcher: Fetcher<ARGS, BODY>,
  args: ARGS,
): void {
  // Nothing to preload, it's already known.
  if (_cache.get(fetcher, args)) return
  initNewFetch(fetcher, args).catch(() => {})
}

type UpdateFunction<BODY> = (existing: BODY | undefined) => BODY

/**
 * Manually update the Suspense Cache's data, often after getting fresh data
 * from the server.
 * An update function taking the current value (or undefined) can also be passed.
 */
export function updateData<ARGS extends readonly SerializableArg[], BODY>(
  fetcher: Fetcher<ARGS, BODY>,
  args: ARGS,
  value: BODY | UpdateFunction<BODY>,
  /* Use notify: false if you need to run a transaction across different systems (like Suspense followed by router)
     so that only the last step in that transaction actually end up re-rendering the view with a consistent state. */
  options?: { notify?: boolean },
): void {
  const { notify = true } = options ?? {}

  if (typeof value === "function") {
    const existing = _cache.get(fetcher, args)
    const body =
      existing?.state === "fetched" && existing.ok ? existing.body : undefined
    value = (value as UpdateFunction<BODY>)(body)
  }

  _cache.set(fetcher, args, {
    state: "fetched",
    ok: true,
    body: value,
    args,
    // Note: we leave stale to undefined here as it's assumed you're a good person and provide explicit, non stale data 🤓
  })

  if (notify) notifyAll(fetcher, args)
}

/**
 * Revalidates specific fetcher's data from the server, immediately.
 * Only use this if this data has mega high priority and you want to refetch it as soon as possible, even if no components currently use it.
 */
export async function revalidateData<
  ARGS extends readonly SerializableArg[],
  BODY,
>(fetcher: Fetcher<ARGS, BODY>, args: ARGS): Promise<void> {
  return revalidate(fetcher, args)
    ?.then(() => notifyAll(fetcher, args))
    .catch(() => {})
}

/**
 * Invalidates all fetcher's data: If no requests are currently active/mounted,
 * the data will only be re-fetched the next time a component requesting it is mounted.
 * Usually, prefer this over revalidate.
 */
export function invalidateFetcherData<ARGS extends readonly SerializableArg[]>(
  fetcher: Fetcher<ARGS>,
) {
  const fetcherMap = _cache.getFetcherMap(fetcher)
  if (!fetcherMap) return

  Array.from(fetcherMap.values()).forEach((value) => (value.stale = true))
  notifyAll(fetcher)
}

export function invalidateData<ARGS extends readonly SerializableArg[], BODY>(
  fetcher: Fetcher<ARGS, BODY>,
  args: ARGS,
) {
  const cacheValue = _cache.get(fetcher, args)
  if (!cacheValue) return

  cacheValue.stale = true
  notifyAll(fetcher, args)
}

/**
 * Returns all cached data known for a given fetcher, in access chronological order (though you probably should not rely on this)
 * You usually only need this when implementing an accumulative list of data such as an infinite scroll.
 */
export function useFetcherData<ARGS extends readonly SerializableArg[], BODY>(
  fetcher: Fetcher<ARGS, BODY>,
): BODY[] {
  useSubscribeToUpdates(fetcher, () => true)

  const fetcherMap = _cache.getFetcherMap(fetcher)
  if (!fetcherMap) return []

  return [...fetcherMap.values()].flatMap((value) => {
    if (value.state === "fetching") return []
    if (!value.ok) return []
    return value.body
  })
}

/**
 * Update or completely remove any cached fetcher data.
 */
export function updateFetcherData<
  ARGS extends readonly SerializableArg[],
  BODY,
>(
  fetcher: Fetcher<ARGS, BODY>,
  updateFunction: (body: BODY) => BODY | undefined,
) {
  const fetcherMap = _cache.getFetcherMap(fetcher)
  if (!fetcherMap) return

  for (const [key, value] of fetcherMap) {
    if (value.state !== "fetched") continue
    if (!value.ok) continue

    const updatedBody = updateFunction(value.body)
    if (updatedBody === undefined) fetcherMap.delete(key)
    else fetcherMap.set(key, { ...value, body: updatedBody })
  }

  notifyAll(fetcher)
}

function notifyAll<ARGS extends readonly SerializableArg[], BODY>(
  fetcher: Fetcher<ARGS, BODY>,
  args?: ARGS,
) {
  cacheSubscribers.forEach((sub) => sub(fetcher, args))
}

// Note: Only use this if there's no cache entry yet for this combination.
function initNewFetch<ARGS extends readonly SerializableArg[], BODY>(
  fetcher: Fetcher<ARGS, BODY>,
  args: ARGS,
) {
  const { promise, start } = scheduleFetch(fetcher, args)

  _cache.set(fetcher, args, {
    state: "fetching",
    promise,
    start,
    args,
  })

  return promise
}

function scheduleFetch<ARGS extends readonly SerializableArg[], BODY>(
  fetcher: Fetcher<ARGS, BODY>,
  args: ARGS,
) {
  const fetcherMap = _cache.getFetcherMap(fetcher)
  const { concurrency } = _cache.getFetcherConfig(fetcher)

  // Brand new fetch: ignore concurrency.
  if (!fetcherMap)
    return {
      promise: fetchThenStartNext(fetcher, args),
    }

  const inFlightCount = count(fetcherMap.values(), isInFlight)

  // We have a few in-flight requests already but still below the max.
  if (inFlightCount < concurrency)
    return {
      promise: fetchThenStartNext(fetcher, args),
    }

  // We're at capacity, wait.
  const [wait, start] = makeDeferred()

  return {
    promise: wait.then(() => fetchThenStartNext(fetcher, args)),
    start,
  }
}

function fetchThenStartNext<ARGS extends readonly SerializableArg[], BODY>(
  fetcher: Fetcher<ARGS, BODY>,
  args: ARGS,
) {
  return fetcher(...args)
    .then((body) => {
      _cache.set(fetcher, args, {
        state: "fetched",
        ok: true,
        body,
        args,
        stale: _cache.get(fetcher, args)?.stale,
      })

      return body
    })
    .catch((error: unknown) => {
      _cache.set(fetcher, args, {
        state: "fetched",
        ok: false,
        error,
        args,
        stale: _cache.get(fetcher, args)?.stale,
      })
    })
    .finally(() => {
      const fetcherMap = _cache.getFetcherMap(fetcher)
      if (!fetcherMap) return

      const next = find(fetcherMap.values(), isPending)
      next?.start()
    })
}

function useRegisterRequestWithBoundary<
  ARGS extends readonly SerializableArg[],
  BODY,
>(fetcher: Fetcher<ARGS, BODY>, args: ARGS) {
  const errorBoundary = useContext(ErrorBoundaryContext)

  if (!errorBoundary) {
    console.warn(
      "It is not recommended to use editor-support->Suspense without a editor-ui->ErrorBoundary above it.",
    )
    return
  }

  errorBoundary.registerRequest(fetcher, args)
}

function useSubscribeToUpdates<ARGS extends readonly SerializableArg[], BODY>(
  fetcher: Fetcher<ARGS, BODY>,
  shouldUpdate: (updatedArgs: ARGS) => boolean,
): void {
  const forceUpdate = useForceUpdate()

  useEffect(() => {
    function onUpdate(forFetcher: Fetcher, forArgs?: readonly unknown[]) {
      if (fetcher !== forFetcher) return
      if (forArgs && !shouldUpdate(forArgs as ARGS)) return
      forceUpdate()
    }

    cacheSubscribers.push(onUpdate)

    return () => {
      const index = cacheSubscribers.indexOf(onUpdate)
      cacheSubscribers.splice(index, 1)
    }
  }, [fetcher, forceUpdate, shouldUpdate])
}

function useRevalidateDataOnFocus<
  ARGS extends readonly SerializableArg[],
  BODY,
>(fetcher: Fetcher<ARGS, BODY>, args: ARGS) {
  const forceUpdate = useForceUpdate()

  useTabFocus(() => {
    const strategy = _cache.getFetcherConfig(fetcher).revalidationStrategy
    if (strategy?.type === "never" || !strategy?.onFocus) return

    revalidate(fetcher, args)
      ?.then(forceUpdate)
      .catch(() => {})
  })
}

function revalidate<ARGS extends readonly SerializableArg[], BODY>(
  fetcher: Fetcher<ARGS, BODY>,
  args: ARGS,
  reason?: "wasStale",
) {
  const cacheValue = _cache.get(fetcher, args)

  // No need to do anything if the value has been evicted or it's already being fetched/revalidated.
  if (!cacheValue || cacheValue.state !== "fetched") return

  const { promise, start } = scheduleFetch(fetcher, args)

  _cache.set(fetcher, args, {
    ...cacheValue,
    state: "revalidating",
    stale: reason === "wasStale" ? undefined : cacheValue.stale,
    promise,
    start,
  })

  return promise
}

export type Fetcher<
  ARGS extends readonly SerializableArg[] = never,
  BODY = unknown,
> = (...args: ARGS) => Promise<BODY>

interface FetcherConfig {
  /**
   * How many items to keep in the LRU cache.
   * Defaults to 500. Note that this must be at least as great as the number of displayed components using distinct data from this fetcher.
   */
  cacheCapacity: number

  /**
   * A value of "never" indicates that once cached, the value will always be returned as it was, without any revalidation.
   * A value of swr (stale-while-revalidate) means data is reused immediately if available but revalidated at various point in time or on certain actions.
   */
  revalidationStrategy: { type: "never" } | { type: "swr"; onFocus: boolean }

  /**
   * The max concurrency for this fetcher.
   * Defaults to 10.
   * Set this to a minimum of 1. Infinity is also supported.
   */
  concurrency: number
}

type CacheValue<BODY = unknown> =
  | FetchedSuccess<BODY>
  | FetchedError
  | InFlight<BODY>

interface CacheMeta {
  args: readonly SerializableArg[]
  stale?: true
}

interface Fetching<BODY = unknown> extends CacheMeta {
  state: "fetching"
  promise: Promise<BODY>
  start?: () => void
}

interface FetchedSuccess<BODY = unknown> extends CacheMeta {
  state: "fetched"
  ok: true
  body: BODY
}

interface FetchedError extends CacheMeta {
  state: "fetched"
  ok: false
  error: unknown
}

interface RevalidatingSuccess<BODY = unknown> extends CacheMeta {
  state: "revalidating"
  ok: true
  body: BODY
  promise: Promise<BODY>
  start?: () => void
}

interface RevalidatingError<BODY = unknown> extends CacheMeta {
  state: "revalidating"
  ok: false
  error: unknown
  promise: Promise<BODY>
  start?: () => void
}

type InFlight<BODY = unknown> = Fetching<BODY> | Revalidating<BODY>
type Revalidating<BODY = unknown> =
  | RevalidatingSuccess<BODY>
  | RevalidatingError<BODY>

const _cache = createSuspenseCache()

type PublicCache = Pick<
  typeof _cache,
  | "registerFetcherConfig"
  | "clear"
  | "clearAll"
  | "clearAllFetcherConfigs"
  | "has"
>
export const cache: PublicCache = _cache

export function createSuspenseCache() {
  const fetcherConfig = new Map<Fetcher, FetcherConfig>()
  const map = new Map<Fetcher, Map<string, CacheValue>>()

  const defaultConfig: FetcherConfig = {
    cacheCapacity: 500,
    revalidationStrategy: { type: "swr", onFocus: false },
    concurrency: 10,
  }

  function getFetcherConfig(fetcher: Fetcher): FetcherConfig {
    return fetcherConfig.get(fetcher) ?? defaultConfig
  }

  return {
    get<ARGS extends readonly SerializableArg[], BODY>(
      fetcher: Fetcher<ARGS, BODY>,
      args: ARGS,
    ): CacheValue<BODY> | undefined {
      const key = argsKey(fetcher, args)
      const fetcherMap = map.get(fetcher) as
        | Map<string, CacheValue<BODY>>
        | undefined

      if (!fetcherMap || !fetcherMap.has(key)) return undefined

      // LRU: since we just accessed this key, move it to the last position.
      const value = fetcherMap.get(key)
      fetcherMap.delete(key)
      if (value !== undefined) fetcherMap.set(key, value)

      return value
    },

    getFetcherMap<BODY>(fetcher: Fetcher<never, BODY>) {
      return map.get(fetcher) as Map<string, CacheValue<BODY>> | undefined
    },

    set<ARGS extends readonly SerializableArg[]>(
      fetcher: Fetcher<ARGS>,
      args: ARGS,
      value: CacheValue,
    ): void {
      let fetcherMap = map.get(fetcher)

      if (!fetcherMap) {
        fetcherMap = new Map()
        map.set(fetcher, fetcherMap)
      }

      const config = getFetcherConfig(fetcher)
      const key = argsKey(fetcher, args)

      // Delete the least recently used key to make room for this new key.
      if (fetcherMap.size === config.cacheCapacity && !fetcherMap.has(key))
        fetcherMap.delete(fetcherMap.keys().next().value as string)

      fetcherMap.set(key, value)
    },

    /**
     * Returns whether the Suspense cache currently has available data for a particular fetcher & args combination.
     */
    has<ARGS extends readonly SerializableArg[], BODY>(
      fetcher: Fetcher<ARGS, BODY>,
      args: ARGS,
    ): boolean {
      const key = argsKey(fetcher, args)
      const fetcherMap = map.get(fetcher)

      const value = fetcherMap?.get(key)
      if (!value) return false

      const isFetched = value.state === "fetched"
      const wasFetched = value.state === "revalidating" && value.ok

      return isFetched || wasFetched
    },

    getFetcherConfig,

    registerFetcherConfig(
      fetcher: Fetcher,
      config: Partial<FetcherConfig>,
    ): void {
      fetcherConfig.set(fetcher, { ...defaultConfig, ...config })
    },

    clear<ARGS extends readonly SerializableArg[]>(
      fetcher: Fetcher<ARGS>,
      args?: ARGS,
    ): void {
      if (!args) {
        // We want to delete the whole cache for the fetcher
        map.delete(fetcher)
        return
      }

      const fetcherMap = map.get(fetcher)
      if (!fetcherMap) return

      const key = argsKey(fetcher, args)
      fetcherMap.delete(key)
    },

    clearAll(): void {
      map.clear()
    },

    // Only for tests
    clearAllFetcherConfigs(): void {
      fetcherConfig.clear()
    },
  }
}

export function argsKey(fetcher: Fetcher, args: readonly unknown[]) {
  const lengthDiff = fetcher.length - args.length

  if (lengthDiff === 0) return args.join("\n")

  // We allow fetchers with completely optional arguments (?:)
  // For any missing args, add one explicit undefined so the key is always the same.
  const paddedArgs = [...args]
  for (let i = 0; i < lengthDiff; i++) {
    paddedArgs.push(undefined)
  }

  return paddedArgs.join("\n")
}

// Fetcher args are serialized inside a Map. Only allow types that have a reasonnable toString() implementation.
type SerializableArg = string | number | boolean | URL | undefined

const cacheSubscribers: ((
  fetcher: Fetcher,
  args?: readonly unknown[],
) => void)[] = []

interface ErrorBoundaryAPI {
  /* Remember which Suspense requests are used under this boundary so we can clear the exact number of cache entries.  */
  registerRequest: (fetcher: Fetcher, args: readonly unknown[]) => void
  /* Only call this after an error. Clears the global Suspense Cache keys that this error boundary has seen since the last error. */
  reset: () => void
}

export const ErrorBoundaryContext = createContext<ErrorBoundaryAPI | undefined>(
  undefined,
)

interface UseCacheRevalidationArgs<
  ARGS extends readonly SerializableArg[],
  BODY,
> {
  fetcher: Fetcher<ARGS, BODY>
  requestParams: ARGS
  validator: (response: BODY, cachedResponse: BODY) => boolean
  onCacheInvalidated: () => void
  /**
   * Run the cache revalidation every x amount of ms
   * @default null - no periodical validation
   */
  every?: number | null
}

/**
 * This hook revalidates data from the server when the tab gains focus and at regular intervals.
 *
 * Use this hook when you want to check for new data available to the user without making a direct request,
 * such as with `useRequest`, which would update the local cache.
 *
 * This approach allows us to inform the user about the availability of new data without disrupting their current work with a new data response.
 *
 * The hook continuously validates the data in the user's local cache against newly fetched data.
 * When the validator function returns `true`, the `onCacheInvalidated` callback is triggered.
 *
 * It's up to you to handle any effects based on the validation and to update the cache to resolve the invalid cache state.
 *
 * @param args
 * @param args.fetcher The fetcher to revalidate.
 * @param args.requestParams The request's parameters.
 * @param args.validator A function that returns true if the data needs to be revalidated.
 * @param args.onCacheInvalidated A function that is called when the cache is invalidated.
 * @param args.every A number dictating how often the validation must run in ms.
 */
export function useCacheRevalidation<
  ARGS extends readonly SerializableArg[],
  BODY,
>(args: UseCacheRevalidationArgs<ARGS, BODY>) {
  const {
    fetcher,
    requestParams,
    validator,
    onCacheInvalidated,
    every = null,
  } = args

  /**
   * This function:
   * 1. Reads data from the cache
   *    (according to requestParams from state or passed as an argument,
   *     the second case is useful in situations where the state
   *     has not been updated yet (as state in React is async))
   * 2. Fetches data from the server
   * 3. Compares the data from the cache with the data from the server
   * 4. Calls the onCacheInvalidated callback if the data is different
   */
  function revalidateData(requestParamsOverride?: ARGS) {
    const params = requestParamsOverride ?? requestParams
    // Retrieve cache based on the passed fetcher and params
    const cache = _cache.get(fetcher, params)
    // Retrieve data directly from the server instead of the cache.
    // This allows us to compare the local data with the server data.
    fetcher(...params)
      .then((response) => {
        // Pass response to validator alongside the local cache
        if (
          cache?.state === "fetched" &&
          cache.ok &&
          validator(response, cache.body)
        )
          onCacheInvalidated()
      })
      .catch((err) => console.warn(err))
  }

  useTabFocus(revalidateData)
  useInterval(revalidateData, every)

  return { revalidateData }
}

function makeDeferred() {
  let resolve: (ignored?: unknown) => void

  const promise = new Promise((_resolve) => {
    resolve = _resolve
  })

  return [promise, () => resolve()] as const
}

function isInFlight(value: CacheValue): value is InFlight {
  return value.state === "fetching" || value.state === "revalidating"
}

function isPending(
  value: CacheValue,
): value is InFlight & { start: () => void } {
  return isInFlight(value) && Boolean(value.start)
}
